import React, { useEffect, useState } from 'react';
import { Typography, Breadcrumb, Card, Calendar, Badge, Modal, Form, InputNumber, DatePicker, Button, Space, message, Popconfirm } from 'antd';
import { useAuth } from '../../../contexts/AuthContext';

const { Title, Paragraph } = Typography;

const SchedulingCalendar = () => {
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { token } = useAuth();

  useEffect(() => {
    const load = async () => {
      try {
        if (!token) return;
        setLoading(true);
        const res = await fetch('/api/budget-events', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to load events');
        const data = await res.json();
        setEvents(Array.isArray(data) ? data : []);
      } catch (e) {
        message.error(e?.message || 'Failed to load events');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const onDelete = async (evt) => {
    try {
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(`/api/budget-events/${evt.id}` , {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to delete');
      setEvents(prev => prev.filter(e => e.id !== evt.id));
      message.success('Event deleted');
    } catch (e) {
      message.error(e?.message || 'Delete failed');
    }
  };

  const dateCellRender = (value) => {
    const dateStr = value.format('YYYY-MM-DD');
    const dayEvents = events.filter(e => e.date === dateStr);
    return (
      <ul className="events" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {dayEvents.map((e) => (
          <li key={e.id || `${e.date}-${e.label}-${e.amount}`} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Badge status="processing" text={`${e.label} ($${e.amount})`} />
            {e.id && (
              <Popconfirm title="Delete this event?" onConfirm={() => onDelete(e)} okText="Delete" okButtonProps={{ danger: true }}>
                <Button size="small" type="text" danger style={{ padding: 0, height: 22 }}>Delete</Button>
              </Popconfirm>
            )}
          </li>
        ))}
      </ul>
    );
  };

  const onAdd = async () => {
    try {
      const vals = await form.validateFields();
      const dateStr = vals.date.format('YYYY-MM-DD');
      if (!token) throw new Error('Not authenticated');
      setSaving(true);
      const res = await fetch('/api/budget-events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ date: dateStr, label: vals.label, amount: vals.amount })
      });
      if (!res.ok) throw new Error('Failed to add event');
      const created = await res.json();
      setEvents(prev => [created, ...prev]);
      setOpen(false);
      form.resetFields();
      message.success('Scheduled spend added');
    } catch (e) {
      // ignore form errors
      if (e?.message && !e?.errorFields) message.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[{ title: 'Client' }, { title: 'Budgets' }, { title: 'Calendar' }]} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Scheduling Calendar</Title>
        <Space>
          <Button type="primary" onClick={() => setOpen(true)}>Add Scheduled Spend</Button>
        </Space>
      </div>

      <Card>
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>Plan campaign spend across dates.</Paragraph>
        <Calendar dateCellRender={dateCellRender} loading={loading ? 1 : undefined} />
      </Card>

      <Modal
        open={open}
        title="Add Scheduled Spend"
        onCancel={() => setOpen(false)}
        onOk={onAdd}
        confirmLoading={saving}
      >
        <Form layout="vertical" form={form}>
          <Form.Item label="Date" name="date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Label" name="label" rules={[{ required: true }]}>
            <input className="ant-input" placeholder="e.g., Facebook Boost" />
          </Form.Item>
          <Form.Item label="Amount (USD)" name="amount" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SchedulingCalendar;
